﻿import {
  CreditCard,
  Edit2,
  MapPin,
  MessageCircle,
  Phone,
  Receipt,
  Save,
  Store,
  X,
  Link as LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function SettingsForm({
  formData,
  setFormData,
  handleChange,
  isEditing,
  setIsEditing,
  updateSettings,
  handleSubmit,
  resetForm,
  isAdmin,
}: any) {
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!isAdmin && (
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-500 p-4 rounded-xl flex items-center text-sm">
          Apenas administradores podem editar as configurações do lava-jato.
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg space-y-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
          <Store className="text-blue-500" size={20} />
          Dados Principais
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <LinkIcon size={16} className="text-blue-400" />
              Link de Agendamento Público
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-slate-700 bg-slate-800 text-slate-400 sm:text-sm">
                autowashpro.com.br/agendar/
              </span>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="rounded-l-none bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
                placeholder="meu-lava-jato"
                disabled={!isEditing}
              />
            </div>
            <p className="text-xs text-slate-500">
              Seus clientes usarão este link para agendar serviços. Use apenas letras, números e hífens.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Nome da Empresa / Lava-Jato
            </label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
              placeholder="AutoWash Pro"
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Slogan / Subtítulo
            </label>
            <Input
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
              placeholder="Estética Automotiva"
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              CPF ou CNPJ
            </label>
            <Input
              name="cnpj"
              value={formData.cnpj}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
              placeholder="00.000.000/0001-00"
              maxLength={18}
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Chave PIX (Para o recibo)
            </label>
            <div className="relative">
              <CreditCard
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                size={16}
              />
              <Input
                name="pix_key"
                value={formData.pix_key}
                onChange={handleChange}
                className="pl-10 bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
                placeholder="CNPJ, Celular ou E-mail"
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg space-y-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
          <Phone className="text-blue-500" size={20} />
          Contato e Endereço
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Telefone / WhatsApp
            </label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
              placeholder="(11) 99999-9999"
              maxLength={15}
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Endereço Completo
            </label>
            <div className="relative">
              <MapPin
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                size={16}
              />
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="pl-10 bg-slate-950 border-slate-800 text-white disabled:opacity-75 disabled:cursor-default"
                placeholder="Rua das Oficinas, 450 - SP"
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg space-y-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
          <Receipt className="text-blue-500" size={20} />
          Impressão e Comunicação
        </h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Receipt size={16} /> Rodapé do Comprovante Térmico
            </label>
            <textarea
              name="receipt_footer"
              value={formData.receipt_footer}
              onChange={handleChange}
              className="w-full bg-slate-950 border border-slate-800 text-white p-3 rounded-xl focus:outline-none focus:border-blue-500 min-h-[80px] disabled:opacity-75 disabled:cursor-default"
              placeholder="Ex: Obrigado pela preferência! Volte sempre."
              disabled={!isEditing}
            />
            <p className="text-xs text-slate-500">
              Essa mensagem sairá na base do recibo impresso (bobina 80mm).
            </p>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <MessageCircle size={16} /> Template Padrão do WhatsApp
            </label>
            <textarea
              name="whatsapp_template"
              value={formData.whatsapp_template}
              onChange={handleChange}
              className="w-full bg-slate-950 border border-slate-800 text-white p-3 rounded-xl focus:outline-none focus:border-blue-500 min-h-[120px] disabled:opacity-75 disabled:cursor-default"
              placeholder="Ex: 🚗 *[NomeEmpresa] - Veículo Pronto!*\n\n..."
              disabled={!isEditing}
            />
            <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-400">
                  Termos inteligentes (clique para inserir no final do texto):
                </p>
                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() =>
                    setFormData((prev: any) => ({
                      ...prev,
                      whatsapp_template:
                        "🚗 *[NomeEmpresa] - Veículo Pronto!*\n\nOlá *[NomeCliente]*!\nSeu veículo *[Veiculo]* (Placa: *[Placa]*) está impecável e pronto para retirada!\n\n📑 *OS:* [OS]\n💰 *Valor Total:* R$ [Total]\n\nAguardamos você em nosso estabelecimento!",
                    }))
                  }
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Restaurar Padrão
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "[NomeEmpresa]",
                  "[NomeCliente]",
                  "[Veiculo]",
                  "[Placa]",
                  "[OS]",
                  "[Total]",
                ].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    disabled={!isEditing}
                    onClick={() =>
                      setFormData((prev: any) => ({
                        ...prev,
                        whatsapp_template: `${prev.whatsapp_template || ""} ${tag}`,
                      }))
                    }
                    className="bg-slate-800 text-xs font-mono text-emerald-400 px-2.5 py-1.5 rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-emerald-500/20"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 gap-3">
        {!isEditing ? (
          <Button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={!isAdmin}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 px-8 rounded-xl shadow-lg shadow-blue-900/20 disabled:opacity-50"
          >
            <Edit2 className="mr-2" size={20} /> Editar Configurações
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={resetForm}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white h-12 px-6 rounded-xl"
            >
              <X className="mr-2" size={20} /> Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateSettings.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 px-8 rounded-xl shadow-lg shadow-emerald-900/20"
            >
              {updateSettings.isPending ? (
                "Salvando..."
              ) : (
                <>
                  <Save className="mr-2" size={20} /> Salvar Alterações
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
